/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Popper, Stack } from '@mui/material';
import { FC, useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AccountMenu from '../../components/AccountMenu/AccountMenu';
import InitialsCircle from '../../components/InitialsCircle/InitialsCircle';
import { selectUserContactDetails, selectIsThisTfo } from '../../../azure-b2c/storage';
import TestIds from '../../../../testIds';
import StyledSelectedAccount from './components/StyledSelectedAccount';

import './NavbarAccountMenu.scss';

const NavbarAccountMenu: FC = () => {
  const userDetails = useSelector(selectUserContactDetails);
  const isTfo = useSelector(selectIsThisTfo);
  const location = useLocation();
  const popperRef = useRef<HTMLDivElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number>(400);

  const openPopper = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const calculateMaxHeight = (): void => {
    if (anchorEl) {
      const anchorRect = anchorEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - anchorRect.bottom - 20;

      setMaxHeight(spaceBelow);
    }
  };

  const closePopper = (): void => setAnchorEl(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        openPopper &&
        popperRef.current &&
        anchorEl &&
        event.target instanceof Node &&
        !popperRef.current.contains(event.target) &&
        !anchorEl.contains(event.target)
      ) {
        closePopper();
      }
    },
    [openPopper, anchorEl],
  );

  useEffect(() => {
    closePopper();
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = (): void => {
      if (openPopper) {
        calculateMaxHeight();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (openPopper && event.key === 'Escape') {
        closePopper();
      }
    };

    if (openPopper) {
      window.addEventListener('resize', handleResize);
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
    }

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopper]);

  return (
    <Stack
      direction='row'
      alignItems='center'
      spacing='16px'
      data-testid={TestIds.feat.account.navbarAccountMenu.component}
    >
      <StyledSelectedAccount
        enableAccountSwitcher
        buttonProps={{ color: 'tertiary' }}
        truncateAccountName
      />

      <InitialsCircle
        fullName={[userDetails?.firstName, userDetails?.lastName]}
        aria-describedby={openPopper ? TestIds.feat.account.navbarAccountMenu.popper : undefined}
        onClick={handleClick}
        invert
      />

      <Popper
        data-testid={TestIds.feat.account.navbarAccountMenu.popper}
        id={TestIds.feat.account.navbarAccountMenu.popper}
        open={openPopper}
        anchorEl={anchorEl}
        placement='bottom-end'
        className='fl-navbar-account-menu__popper'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 16],
            },
          },
        ]}
      >
        <Box ref={popperRef}>
          <AccountMenu
            enableLogout
            enableAccountDetails
            enableActivities={isTfo}
            style={{
              maxHeight: `${maxHeight}px`,
            }}
            divider={true}
          />
        </Box>
      </Popper>
    </Stack>
  );
};

export default NavbarAccountMenu;
